import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { CalendarDays, LayoutList } from 'lucide-react';
import { API_URL } from '../../../config';
import DealRecipientCompany from '../../../components/ui/dealRecipientCompany';
import { useDealCardCollection } from '../../../hooks/deal-card';

const LatestDeals = () => {
  const { t } = useTranslation();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDeals = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/latest-deals`, { withCredentials: true });
        setDeals(data?.deals || []);
      } catch (err) {
        setError(t('app:dashboard.latestDeals.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchDeals();
  }, [t]);

  const { items, surfaceConfig } = useDealCardCollection({
    deals,
    surfaceKey: 'dashboardLatest',
  });

  if (loading) {
    return (
      <section className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
        <p className="text-sm text-stone-500">{t('app:dashboard.latestDeals.loading')}</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="rounded-lg border border-red-100 bg-white p-5 shadow-sm">
        <p className="text-sm text-red-500">{error}</p>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-medium text-stone-600">
            <CalendarDays className="h-3.5 w-3.5" />
            {t('app:dashboard.latestDeals.badge')}
          </div>
          <h3 className="text-xl font-semibold tracking-tight text-stone-950">
            {t('app:dashboard.t2')}
          </h3>
          <p className="mt-2 text-sm leading-6 text-stone-600">
            {t('app:dashboard.latestDeals.subtitle')}
          </p>
        </div>
        <div className="inline-flex h-8 shrink-0 items-center justify-center gap-2 rounded-full border border-stone-200 bg-white px-3 text-xs font-medium text-stone-700 shadow-sm">
          <LayoutList className="h-3.5 w-3.5" />
          <span>{t('app:dashboard.latestDeals.count', { count: items.length })}</span>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="mt-5 rounded-lg border border-dashed border-stone-300 bg-stone-50/70 px-4 py-3 text-sm leading-6 text-stone-600">
          {t('app:dashboard.latestDeals.empty')}
        </div>
      ) : (
        <div className="mt-5 max-h-[620px] overflow-y-auto pr-1">
          <ul className="space-y-3">
            {items.map((deal) => (
              <li
                key={deal.id}
                className="rounded-lg border border-stone-200 bg-stone-50/70 p-4"
              >
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <h4 className="text-sm font-semibold leading-5 text-stone-950">
                        {deal.title || '—'}
                      </h4>

                      {(deal.hasRecipientCompanyInfo || deal.recipientCompany || deal.recipientCompanyUrl) && (
                        <div className="mt-2">
                          <DealRecipientCompany
                            companyName={deal.recipientCompany}
                            companyUrl={deal.recipientCompanyUrl}
                            hasInfo={deal.hasRecipientCompanyInfo}
                            accessMode={surfaceConfig.recipientCompany.accessMode}
                            className="text-xs"
                          />
                        </div>
                      )}
                    </div>

                    <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                      {deal.amount ? (
                        <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                          {deal.amount}
                        </span>
                      ) : null}
                      {deal.round ? (
                        <span className="inline-flex items-center rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-700">
                          {deal.round}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {deal.industries.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {deal.industries.slice(0, 3).map((industry) => (
                        <span
                          key={`${deal.id}-${industry}`}
                          className="inline-flex max-w-full items-center rounded-full bg-white px-3 py-1 text-xs font-medium text-stone-600 ring-1 ring-stone-200"
                        >
                          <span className="truncate">{industry}</span>
                        </span>
                      ))}
                      {deal.industries.length > 3 ? (
                        <span className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-medium text-stone-500 ring-1 ring-stone-200">
                          {t('app:dashboard.latestDeals.moreIndustries', { count: deal.industries.length - 3 })}
                        </span>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="text-xs text-stone-500">
                    {deal.announcementDate || '—'}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default LatestDeals;
